/**
 * Radial-gradient glows overlaid on HighlightBanner. Centers/radii are
 * normalized to the banner box: x/y in [-1, 1] (Flutter Alignment), radius
 * is a fraction of the shorter side.
 */
export interface Glow {
  /** Horizontal center, -1 (left edge) → 1 (right edge). */
  cx: number;
  /** Vertical center, -1 (top edge) → 1 (bottom edge). */
  cy: number;
  /** Radius as a fraction of the shorter side (Flutter convention). */
  radius: number;
}

export const HIGHLIGHT_GLOWS: Glow[] = [
  {cx: -0.024, cy: -1.749, radius: 1.049},
  {cx: -0.779, cy: 1.437, radius: 0.697},
  {cx: -0.213, cy: -1.628, radius: 0.554},
  {cx: 0.746, cy: 2.003, radius: 1.084},
];

/**
 * Reduced glow set used for the split-panel layout: a single bottom-left
 * burst and a single top-right burst. The narrower left panel doesn't have
 * room for the full 4-glow stack, so we keep just the two that read.
 */
export const SPLIT_PANEL_GLOWS: Glow[] = [
  // Bottom-left.
  {cx: -0.6, cy: 1.4, radius: 0.9},
  // Top-right.
  {cx: 0.8, cy: -1.0, radius: 0.9},
];

/**
 * Banner height in px — Flutter's glow radii are normalized to the shorter
 * side of the box (also 113 there), so we use this to convert them to a
 * concrete pixel radius for the CSS gradient.
 */
const BANNER_HEIGHT_PX = 113;

/**
 * Build a CSS `radial-gradient(...)` string for one glow + a 2-stop color
 * list. The first color is the inner glow, the second is the outer fade.
 *
 * Note: radial-gradient `circle <size>` requires a CSS length (not a
 * percentage). We convert Flutter's normalized radius to pixels using the
 * fixed banner height.
 */
const buildGlowCss = (glow: Glow, colors: string[]): string => {
  const [inner, outer] = colors;
  const fade = outer ?? 'transparent';
  const cx = ((glow.cx + 1) / 2) * 100;
  const cy = ((glow.cy + 1) / 2) * 100;
  const radiusPx = (glow.radius * BANNER_HEIGHT_PX).toFixed(1);
  return `radial-gradient(circle ${radiusPx}px at ${cx}% ${cy}%, ${inner} 0%, ${fade} 100%)`;
};

export const buildGlowsCss = (
  glows: Glow[],
  colors: string[] | undefined,
): string => {
  if (!colors || colors.length === 0) return 'none';
  return glows.map(g => buildGlowCss(g, colors)).join(', ');
};
